import { useState } from "react";
import { ArrowUp, ArrowDown, EyeOff, Plus, LayoutGrid, GripVertical, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const arrange = (cards, layout) => {
  const ids = cards.map((c) => c.id);
  const order = [...(layout?.order || []).filter((id) => ids.includes(id)), ...ids.filter((id) => !(layout?.order || []).includes(id))];
  const hidden = layout?.hidden ?? cards.filter((c) => c.defaultHidden).map((c) => c.id);
  return { order, hidden: hidden.filter((id) => ids.includes(id)) };
};

const Tools = ({ c, i, n, move, hide, drag, dragEnd }) => (
  <div className="flex items-center gap-1 mb-2 px-3 py-2 rounded-2xl bg-[#2C4A3E] text-white text-xs" data-testid={`dash-tools-${c.id}`}>
    <span draggable onDragStart={drag} onDragEnd={dragEnd} className="cursor-grab p-1" aria-label="Drag to move"><GripVertical className="w-4 h-4" /></span>
    <span className="font-semibold flex-1">{c.label}</span>
    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`dash-up-${c.id}`} className="p-1.5 rounded-full hover:bg-white/15 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
    <button type="button" disabled={i === n - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`dash-down-${c.id}`} className="p-1.5 rounded-full hover:bg-white/15 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
    <button type="button" onClick={hide} aria-label="Hide card" data-testid={`dash-hide-${c.id}`} className="p-1.5 rounded-full hover:bg-white/15"><EyeOff className="w-4 h-4" /></button>
  </div>
);

export const DashboardLayout = ({ cards, initial }) => {
  const [lay, setLay] = useState(() => arrange(cards, initial));
  const [edit, setEdit] = useState(false);
  const [dragId, setDragId] = useState(null);
  const { order, hidden } = arrange(cards, lay);
  const byId = Object.fromEntries(cards.map((c) => [c.id, c]));
  const shown = order.filter((id) => !hidden.includes(id));
  const save = (next) => { setLay(next); http.put("/auth/me", { dashboard_layout: next }).catch(() => toast.error("Couldn't save your layout")); };
  const move = (i, d) => { const o = [...shown]; [o[i], o[i + d]] = [o[i + d], o[i]]; save({ order: [...o, ...hidden], hidden }); };
  const dropOn = (target) => {
    if (!dragId || dragId === target) { setDragId(null); return; }
    const o = shown.filter((x) => x !== dragId); o.splice(o.indexOf(target), 0, dragId);
    save({ order: [...o, ...hidden], hidden }); setDragId(null);
  };
  return (
    <div data-testid="dashboard-layout">
      <div className="mt-8 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setEdit(!edit)} data-testid="dash-customize-btn"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${edit ? "bg-[#C87D53] border-[#C87D53] text-white" : "bg-white border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB]"}`}>
          {edit ? <><Check className="w-4 h-4" /> Done</> : <><LayoutGrid className="w-4 h-4" /> Customize my dashboard</>}</button>
        {edit && <button type="button" onClick={() => save({ order: [], hidden: cards.filter((c) => c.defaultHidden).map((c) => c.id) })} data-testid="dash-reset-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs text-stone-600 hover:bg-[#F1EADB]"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>}
      </div>
      {edit && hidden.length > 0 && (
        <div className="mt-3 rounded-2xl border border-dashed border-[#C87D53] bg-[#FBF1EA] p-4" data-testid="dash-add-tray">
          <p className="text-xs uppercase tracking-widest text-[#8A4F2E] font-semibold">Add a card</p>
          <div className="mt-2 flex flex-wrap gap-2">{hidden.map((id) => byId[id] && (
            <button key={id} type="button" onClick={() => save({ order: [...shown, id, ...hidden.filter((x) => x !== id)], hidden: hidden.filter((x) => x !== id) })} data-testid={`dash-add-${id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E6DEC3] text-sm text-[#2C4A3E] hover:-translate-y-0.5 transition-transform"><Plus className="w-3.5 h-3.5" />{byId[id].label}</button>))}</div>
        </div>
      )}
      {shown.map((id, i) => (
        <div key={id} data-testid={`dash-card-${id}`} onDragOver={(e) => edit && e.preventDefault()} onDrop={() => edit && dropOn(id)}
          className={edit ? `mt-6 rounded-3xl p-2 ring-2 ${dragId === id ? "ring-[#C87D53] opacity-60" : "ring-[#E6DEC3]"} transition-[opacity,box-shadow]` : ""}>
          {edit && <Tools c={byId[id]} i={i} n={shown.length} move={move} hide={() => save({ order, hidden: [...hidden, id] })} drag={() => setDragId(id)} dragEnd={() => setTimeout(() => setDragId(null), 0)} />}
          {byId[id].node}
        </div>
      ))}
    </div>
  );
};
